import React, { useState, useEffect } from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"
import * as authzc from "authzcached"
import * as feedback from "brandguard/api/feedback"
import * as api from "brandguard/api"
import * as profiles from "profiles"
import * as httpx from "httpx"
import * as icons from "icons"

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
`

const ModalContainer = styled(layouts.containers.flex)`
	width: 480px;
	max-height: 80vh;
	flex-direction: column;
	background: ${layouts.theme.colors.white};
	border-radius: 10px;
	box-shadow: ${layouts.theme.boxshadow.black.wide};
	overflow: hidden;
`

const ModalHeader = styled(layouts.containers.flex)`
	padding: 16px 20px;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${layouts.theme.borders.grey.dark50alphamedium1px};
	flex-shrink: 0;
`

const ModalBody = styled(layouts.containers.flex)`
	flex: 1;
	flex-direction: column;
	padding: 20px;
	overflow-y: auto;
	min-height: 0;
`

const ModalFooter = styled(layouts.containers.flex)`
	padding: 16px 20px;
	justify-content: flex-end;
	gap: 10px;
	border-top: ${layouts.theme.borders.grey.dark50alphamedium1px};
	flex-shrink: 0;
`

const Title = styled.span`
	font-size: 16px;
	font-weight: 600;
	color: ${layouts.theme.colors.grey.dark50};
`

const FormGroup = styled(layouts.containers.flex)`
	flex-direction: column;
	margin-bottom: 16px;
`

const Label = styled.label`
	font-size: 12px;
	font-weight: 500;
	color: ${layouts.theme.colors.grey.dark50};
	margin-bottom: 4px;
`

const Input = styled.input`
	padding: 10px 12px;
	border: ${layouts.theme.borders.grey.dark50alphamedium1px};
	border-radius: 4px;
	font-size: 14px;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const TextArea = styled.textarea`
	padding: 10px 12px;
	border: ${layouts.theme.borders.grey.dark50alphamedium1px};
	border-radius: 4px;
	font-size: 14px;
	resize: vertical;
	min-height: 60px;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const Select = styled.select`
	padding: 10px 12px;
	border: ${layouts.theme.borders.grey.dark50alphamedium1px};
	border-radius: 4px;
	font-size: 14px;
	background: ${layouts.theme.colors.white};
	cursor: pointer;
	&:focus {
		outline: none;
		border-color: ${layouts.theme.colors.blue.blue};
	}
`

const Message = styled(layouts.containers.flex)<{ success: boolean }>`
	padding: 10px 12px;
	border-radius: 4px;
	font-size: 13px;
	margin-bottom: 16px;
	background: ${(props) => (props.success ? layouts.theme.colors.success : layouts.theme.colors.red.cinnabar)};
	color: ${layouts.theme.colors.white};
`

const CheckboxSection = styled(layouts.containers.flex)`
	flex-direction: column;
	gap: 6px;
	padding: 12px;
	background: ${layouts.theme.colors.grey.lightest};
	border-radius: 6px;
`

const CheckboxRow = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	color: ${layouts.theme.colors.grey.dark50};
	cursor: pointer;
`

const Checkbox = styled.input`
	width: 16px;
	height: 16px;
	cursor: pointer;
`

const ScoreValue = styled.span<{ isNA?: boolean }>`
	margin-left: auto;
	font-size: 12px;
	color: ${(props) => (props.isNA ? layouts.theme.colors.grey.dark20 : layouts.theme.colors.grey.dark50)};
`

const SectionHeader = styled.div`
	font-size: 11px;
	font-weight: 600;
	color: ${layouts.theme.colors.grey.dark50};
	margin-bottom: 2px;
	margin-top: 6px;
	text-transform: uppercase;
	&:first-of-type {
		margin-top: 0;
	}
`

interface ScorecardItem {
	key: string
	label: string
	score: number | undefined
	inverse: boolean
}

function formatScore(score: number | undefined, inverse: boolean): string {
	if (score === undefined || score < 0) {
		return "N/A"
	}
	const displayScore = inverse ? 1 - score : score
	return `${Math.round(displayScore * 100)}%`
}

function isScoreAvailable(score: number | undefined): boolean {
	return score !== undefined && score >= 0
}

interface ImageProps {
	eventType: "image"
	eventId: string
	event: api.ImageEvent
	onClose: () => void
}

interface TextProps {
	eventType: "text"
	eventId: string
	event: api.TextEvent
	onClose: () => void
}

interface DocumentProps {
	eventType: "document"
	eventId: string
	event: api.DocumentEvent
	onClose: () => void
}

type Props = ImageProps | TextProps | DocumentProps

export function SendEmailDialog(props: Props): JSX.Element {
	const { eventType, eventId, onClose, event } = props
	const authzaccount = authzc.useCache((cached) => cached.meta)
	const [recipientEmail, setRecipientEmail] = useState("")
	const [subject, setSubject] = useState("")
	const [message, setMessage] = useState("")
	const [loading, setLoading] = useState(false)
	const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
	const [accountProfiles, setAccountProfiles] = useState<profiles.api.proto.Profile[]>([])
	const [loadingProfiles, setLoadingProfiles] = useState(true)

	useEffect(() => {
		const retry = httpx.autoretry()
		const req = profiles.api.searches.zero({ limit: 100n, enabled: profiles.api.StatusFilters.Enabled })
		const p = retry
			.wrap(() => profiles.api.search(req, authzc.bearer(authzaccount)))
			.then((resp) => {
				const profilesWithEmail = resp.items.filter((profile) => profile.email && profile.email.trim() !== "")
				setAccountProfiles(profilesWithEmail)
			})
			.catch((err) => console.error("unable to load profiles", err))
			.finally(() => setLoadingProfiles(false))
		return p.cancel
	}, [])

	const getScorecardItems = (): { brandPrinciples: ScorecardItem[]; precisionElements: ScorecardItem[]; brandSafety: ScorecardItem[] } => {
		if (eventType === "image") {
			const imgEvent = event as api.ImageEvent
			return {
				brandPrinciples: [
					{ key: "look_and_feel", label: "Look & Feel", score: imgEvent.image_lookandfeel_conformance, inverse: false },
					{ key: "brand_consistency", label: "Brand Consistency", score: imgEvent.artifact_score, inverse: false },
				],
				precisionElements: [
					{ key: "logo_detection", label: "Correct Logo(s)", score: imgEvent.image_logo_detection, inverse: false },
					{ key: "font_detection", label: "Correct Font(s)", score: imgEvent.image_font_detection, inverse: false },
					{ key: "background_colors", label: "Colors", score: imgEvent.image_background_colors, inverse: false },
				],
				brandSafety: [
					{ key: "nudity", label: "Nudity Check", score: imgEvent.sexually_explicit, inverse: true },
					{ key: "violence", label: "Violence Check", score: imgEvent.image_violence, inverse: true },
				],
			}
		} else if (eventType === "text") {
			const txtEvent = event as api.TextEvent
			return {
				brandPrinciples: [
					{ key: "text_consistency", label: "Text Consistency", score: txtEvent.onbrand, inverse: false },
					{ key: "tone_and_voice", label: "Tone & Voice", score: txtEvent.text_tone_and_voice, inverse: false },
				],
				precisionElements: [
					{ key: "grammar", label: "Grammar", score: txtEvent.grammar, inverse: false },
					{ key: "reference_rules", label: "Reference Rules", score: undefined, inverse: false },
				],
				brandSafety: [
					{ key: "racism", label: "Racism Check", score: txtEvent.racism, inverse: true },
					{ key: "profanity", label: "Profanity Check", score: txtEvent.profanity, inverse: false },
				],
			}
		} else {
			const docEvent = event as api.DocumentEvent
			return {
				brandPrinciples: [
					{ key: "text_consistency", label: "Text Consistency", score: docEvent.doc_brand_voice, inverse: false },
					{ key: "brand_consistency", label: "Brand Consistency", score: docEvent.artifact_score, inverse: false },
				],
				precisionElements: [
					{ key: "logo_detection", label: "Correct Logo(s)", score: docEvent.doc_logo_detection, inverse: false },
					{ key: "font_detection", label: "Correct Font(s)", score: docEvent.doc_font_detection, inverse: false },
					{ key: "look_and_feel", label: "Look & Feel", score: docEvent.doc_lookandfeel, inverse: false },
				],
				brandSafety: [
					{ key: "nudity", label: "Nudity Check", score: docEvent.doc_nudity, inverse: true },
					{ key: "violence", label: "Violence Check", score: docEvent.doc_violence, inverse: true },
				],
			}
		}
	}

	const { brandPrinciples, precisionElements, brandSafety } = getScorecardItems()

	const toggleItem = (key: string) => {
		setSelectedItems((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(key)) {
				newSet.delete(key)
			} else {
				newSet.add(key)
			}
			return newSet
		})
	}

	const handleSubmit = async () => {
		if (selectedItems.size === 0) {
			setResult({ success: false, message: "Please select at least one item to include in the email" })
			return
		}
		setLoading(true)
		setResult(null)
		try {
			const resp = await feedback.sendEmail(
				eventType,
				eventId,
				{
					recipient_email: recipientEmail,
					subject,
					message,
					selected_items: Array.from(selectedItems),
				},
				authzc.bearer(authzaccount),
			)
			setResult({ success: resp.success, message: resp.message })
			if (resp.success) {
				setTimeout(onClose, 2000)
			}
		} catch (err) {
			console.error("failed to send email", err)
			setResult({ success: false, message: "Failed to send email. Please try again." })
		} finally {
			setLoading(false)
		}
	}

	const renderCheckboxItem = (item: ScorecardItem) => {
		const isNA = !isScoreAvailable(item.score)
		return (
			<CheckboxRow key={item.key}>
				<Checkbox
					type="checkbox"
					checked={selectedItems.has(item.key)}
					onChange={() => toggleItem(item.key)}
					disabled={isNA}
				/>
				<span>{item.label}</span>
				<ScoreValue isNA={isNA}>{formatScore(item.score, item.inverse)}</ScoreValue>
			</CheckboxRow>
		)
	}

	return (
		<Overlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<ModalHeader>
					<Title>Send Feedback via Email</Title>
					<layouts.containers.flex onClick={onClose} cursor="pointer">
						<icons.Close width="15px" height="15px" fill={layouts.theme.colors.grey.dark50} fillOpacity="0.6" />
					</layouts.containers.flex>
				</ModalHeader>
				<ModalBody>
					{result && <Message success={result.success}>{result.message}</Message>}
					<FormGroup>
						<Label>Select items to include *</Label>
						<CheckboxSection>
							<SectionHeader>Brand Principles</SectionHeader>
							{brandPrinciples.map(renderCheckboxItem)}
							<SectionHeader>Precision Elements</SectionHeader>
							{precisionElements.map(renderCheckboxItem)}
							<SectionHeader>Brand Safety</SectionHeader>
							{brandSafety.map(renderCheckboxItem)}
						</CheckboxSection>
					</FormGroup>
					<FormGroup>
						<Label>Recipient Email *</Label>
						<Select
							value={recipientEmail}
							onChange={(e) => setRecipientEmail(e.target.value)}
							disabled={loadingProfiles}
						>
							<option value="">{loadingProfiles ? "Loading..." : "Select a recipient"}</option>
							{accountProfiles.map((profile) => (
								<option key={profile.id} value={profile.email}>
									{profile.display ? `${profile.display} (${profile.email})` : profile.email}
								</option>
							))}
						</Select>
					</FormGroup>
					<FormGroup>
						<Label>Subject (optional)</Label>
						<Input
							type="text"
							value={subject}
							onChange={(e) => setSubject(e.target.value)}
							placeholder="Brand Compliance Review - Feedback Received"
						/>
					</FormGroup>
					<FormGroup>
						<Label>Message (optional)</Label>
						<TextArea
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder="Add a personal note..."
						/>
					</FormGroup>
				</ModalBody>
				<ModalFooter>
					<layouts.buttons.outline borderRadius="33px" onClick={onClose} disabled={loading}>
						Cancel
					</layouts.buttons.outline>
					<layouts.buttons.primary
						borderRadius="33px"
						onClick={handleSubmit}
						disabled={loading || loadingProfiles || !recipientEmail || selectedItems.size === 0}
					>
						{loading ? "Sending..." : "Send Email"}
					</layouts.buttons.primary>
				</ModalFooter>
			</ModalContainer>
		</Overlay>
	)
}
